import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import FinanceCard from '../../../components/foundation/FinanceCard'
import { financeColors, financeRadius } from '../../../components/tokens'

export interface AccountGroup {
  value: string
  label: string
  children?: Record<string, AccountGroup>
}

export interface TreeNode {
  id: string
  name: string
  type: string
  code: string
  level: number
  children: TreeNode[]
  isExpanded?: boolean
  isSelected?: boolean
  isExpandable?: boolean
}

interface AccountFiltersProps {
  nodes: TreeNode[]
  onNodeSelect: (_node: TreeNode | null) => void
  selectedNodeId: string | null
  onFilterChange: (codePrefix: string | null) => void
}

const classifyNodeTone = (value?: string) => {
  const normalized = (value || '').toLowerCase()

  if (normalized.includes('asset')) {
    return {
      accent: '#2563eb',
      accentSoft: 'rgba(37, 99, 235, 0.12)',
    }
  }

  if (normalized.includes('liabil')) {
    return {
      accent: '#d97706',
      accentSoft: 'rgba(217, 119, 6, 0.14)',
    }
  }

  if (normalized.includes('equity')) {
    return {
      accent: '#7c3aed',
      accentSoft: 'rgba(124, 58, 237, 0.14)',
    }
  }

  if (normalized.includes('income') || normalized.includes('revenue')) {
    return {
      accent: '#059669',
      accentSoft: 'rgba(5, 150, 105, 0.14)',
    }
  }

  if (normalized.includes('expense') || normalized.includes('cost')) {
    return {
      accent: '#dc2626',
      accentSoft: 'rgba(220, 38, 38, 0.12)',
    }
  }

  return {
    accent: financeColors.infoText,
    accentSoft: financeColors.surfaceInfo,
  }
}

const AccountFiltersContainer = styled.div`
  width: 100%;
`

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`

const AllAccountsButton = styled.button<{ $isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  background: ${(props) =>
    props.$isSelected ? financeColors.surfaceInfo : financeColors.surfaceMuted};
  border-radius: ${financeRadius.md};
  border: 1px solid
    ${(props) =>
      props.$isSelected ? financeColors.infoBorder : financeColors.border};
  color: ${financeColors.textStrong};
  font-size: 10px;
  font-weight: 600;
  transition: 0.2s ease;

  &:hover {
    background: ${financeColors.surfaceInfo};
    border-color: ${financeColors.infoBorder};
  }
`

const AllAccountsMeta = styled.span`
  display: inline-flex;
  min-width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.12);
  color: ${financeColors.infoText};
  font-size: 10px;
  font-weight: 700;
`

const AllAccountsLabel = styled.span`
  font-weight: 600;
`

const ClearButton = styled.button`
  background: transparent;
  border: 1px solid ${financeColors.border};
  color: ${financeColors.textMuted};
  font-size: 10px;
  font-weight: 600;
  padding: 5px 9px;
  cursor: pointer;
  border-radius: ${financeRadius.md};

  &:hover {
    background: ${financeColors.surfaceMuted};
    color: ${financeColors.textStrong};
  }
`

const TreeWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: calc(100vh - 332px);
  overflow: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.7);
    border-radius: 999px;
  }
`

const NodeGroup = styled.div<{ $level: number }>`
  position: relative;
  padding-left: ${(props) =>
    props.$level === 0 ? '0' : `${props.$level * 14}px`};

  ${(props) =>
    props.$level > 0 &&
    `
      &::before {
        content: '';
        position: absolute;
        left: ${props.$level * 11 - 5}px;
        top: 0;
        bottom: 0;
        width: 1px;
        background: ${financeColors.border};
        opacity: 0.9;
      }
    `}
`

const NodeButton = styled.div<{
  $selected: boolean
  $level: number
  $accent: string
  $accentSoft: string
}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  padding: ${(props) => (props.$level === 0 ? '8px 10px' : '6px 8px')};
  border-radius: ${financeRadius.md};
  border: 1px solid
    ${(props) => (props.$selected ? props.$accent : financeColors.border)};
  border-left: 3px solid ${(props) => props.$accent};
  background: ${(props) =>
    props.$selected
      ? `linear-gradient(135deg, ${props.$accentSoft}, rgba(255,255,255,0.92))`
      : financeColors.surface};
  color: ${financeColors.textStrong};
  cursor: pointer;
  box-shadow: ${(props) =>
    props.$selected ? '0 8px 18px rgba(15, 23, 42, 0.08)' : 'none'};
  transition: 0.18s ease;

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 2px;
  }

  &:hover {
    border-color: ${(props) => props.$accent};
    background: ${(props) =>
      props.$selected
        ? `linear-gradient(135deg, ${props.$accentSoft}, rgba(255,255,255,0.95))`
        : props.$accentSoft};
  }
`

const NodeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const NodeToggle = styled.button<{ $accent: string; $accentSoft: string }>`
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid ${(props) => props.$accent};
  background: ${(props) => props.$accentSoft};
  color: ${(props) => props.$accent};
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  flex: 0 0 auto;
`

const NodeMarker = styled.span<{
  $level: number
  $selected: boolean
  $accent: string
}>`
  width: ${(props) => (props.$level === 0 ? '10px' : '6px')};
  height: ${(props) => (props.$level === 0 ? '10px' : '6px')};
  border-radius: ${(props) => (props.$level === 0 ? '4px' : '999px')};
  background: ${(props) =>
    props.$selected ? props.$accent : financeColors.borderStrong};
  box-shadow: ${(props) =>
    props.$selected ? '0 0 0 3px rgba(15, 23, 42, 0.08)' : 'none'};
  flex: 0 0 auto;
`

const NodeLabelWrap = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`

const NodeTitle = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: ${financeColors.textStrong};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

const NodeCode = styled.span<{ $accentSoft: string; $accent: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 999px;
  background: ${(props) => props.$accentSoft};
  color: ${(props) => props.$accent};
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
  flex: 0 0 auto;
`

const NodeMeta = styled.div<{ $accentSoft: string; $accent: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: ${(props) => props.$accentSoft};
  color: ${(props) => props.$accent};
  font-size: 10px;
  font-weight: 700;
  flex: 0 0 auto;
`

const ChildWrap = styled.div`
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const EmptyState = styled.div`
  padding: 24px 16px;
  border: 1px dashed ${financeColors.border};
  border-radius: ${financeRadius.md};
  text-align: center;
  color: ${financeColors.textMuted};
  font-size: 12px;
  background: ${financeColors.surfaceMuted};
`

export const AccountFilters: React.FC<AccountFiltersProps> = ({
  nodes,
  onNodeSelect,
  selectedNodeId,
  onFilterChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {}

      const markExpanded = (treeNodes: TreeNode[]) => {
        treeNodes.forEach((node) => {
          if (node.isExpanded || node.level === 0) {
            initial[node.id] = true
          }
          if (node.children?.length) {
            markExpanded(node.children)
          }
        })
      }

      markExpanded(nodes)
      return initial
    },
  )

  const totalNodes = useMemo(() => {
    const count = (treeNodes: TreeNode[]): number =>
      treeNodes.reduce((sum, node) => sum + 1 + count(node.children || []), 0)

    return count(nodes)
  }, [nodes])

  const toggleNode = (id: string) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleNodeSelect = (node: TreeNode) => {
    onNodeSelect(node)
    onFilterChange(node.code)
  }

  const handleAllAccountsClick = () => {
    onNodeSelect(null)
    onFilterChange(null)
  }

  const renderNode = (
    node: TreeNode,
    inheritedTone?: ReturnType<typeof classifyNodeTone>,
  ) => {
    const hasChildren = Boolean(node.children?.length)
    const isExpanded = expandedIds[node.id] ?? node.level === 0
    const isSelected = selectedNodeId === node.id
    const tone =
      node.level === 0
        ? classifyNodeTone(node.type || node.name || node.code)
        : inheritedTone || classifyNodeTone(node.type || node.name || node.code)

    return (
      <NodeGroup key={node.id} $level={node.level}>
        <NodeButton
          role="button"
          tabIndex={0}
          data-testid={`node-${node.id}`}
          $selected={isSelected}
          $level={node.level}
          $accent={tone.accent}
          $accentSoft={tone.accentSoft}
          onClick={() => handleNodeSelect(node)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              handleNodeSelect(node)
            }
          }}
        >
          <NodeContent>
            {hasChildren ? (
              <NodeToggle
                type="button"
                aria-label={
                  isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`
                }
                $accent={tone.accent}
                $accentSoft={tone.accentSoft}
                onClick={(event) => {
                  event.stopPropagation()
                  toggleNode(node.id)
                }}
              >
                {isExpanded ? '−' : '+'}
              </NodeToggle>
            ) : (
              <NodeMarker
                $level={node.level}
                $selected={isSelected}
                $accent={tone.accent}
              />
            )}
            <NodeLabelWrap>
              <NodeTitle title={node.name}>{node.name}</NodeTitle>
              <NodeCode $accent={tone.accent} $accentSoft={tone.accentSoft}>
                {node.code}
              </NodeCode>
            </NodeLabelWrap>
          </NodeContent>
          {hasChildren ? (
            <NodeMeta $accent={tone.accent} $accentSoft={tone.accentSoft}>
              {node.children.length}
            </NodeMeta>
          ) : null}
        </NodeButton>
        {hasChildren && isExpanded ? (
          <ChildWrap>
            {node.children.map((child) => renderNode(child, tone))}
          </ChildWrap>
        ) : null}
      </NodeGroup>
    )
  }

  return (
    <AccountFiltersContainer data-testid="account-filters">
      <FinanceCard
        title="Account Hierarchy"
        description="Browse the chart structure and narrow the table by account family."
        padding="md"
      >
        <FilterHeader>
          <AllAccountsButton
            type="button"
            $isSelected={selectedNodeId === null}
            onClick={handleAllAccountsClick}
            data-testid="all-accounts"
          >
            <AllAccountsLabel>All Accounts</AllAccountsLabel>
            <AllAccountsMeta>{totalNodes}</AllAccountsMeta>
          </AllAccountsButton>
          {selectedNodeId !== null && (
            <ClearButton type="button" onClick={handleAllAccountsClick}>
              Clear
            </ClearButton>
          )}
        </FilterHeader>

        <TreeWrap>
          {nodes.length > 0 ? (
            nodes.map((node) => renderNode(node))
          ) : (
            <EmptyState>No account groups available yet.</EmptyState>
          )}
        </TreeWrap>
      </FinanceCard>
    </AccountFiltersContainer>
  )
}
